/**
 * Judges each student's family-facing notes against each other, for that
 * student only. Saving a note's text runs the judge for that student
 * (`judgeFamilyRepeatAfterSave`). The judgment is stored on the student,
 * so opening the review does not call the model. The review judges only
 * a student whose notes no longer match the stored judgment, for example
 * a note written in the teacher app since.
 */
import { useEffect, useMemo, useState } from "react"

import Firebase from "#classes/Firebase"
import { GptHandler } from "#classes/GptHandler"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import {
  type FamilyRepeatJudgment,
  type FamilyRepeatVerdict,
} from "#customTypes/composedTypes/studentTypes"
import {
  familyRepeatIdsAreCoveredBy,
  type FamilyRepeatInput,
  familyRepeatInputFor,
  familyRepeatInputsByStudent,
  familyRepeatKeyCovers,
  type FamilyRepeatMoment,
  familyRepeatSimilarIds,
} from "#utils/familyRepeatJudgment"

import { historyWindowStart } from "./reviewCardHistory"

const MAX_IN_FLIGHT = 2

let running = 0
const waiting: Array<() => void> = []
const jobs = new Map<string, Promise<void>>()
/** Key this run started from. Missing means the run has not settled. */
const settled = new Map<string, string | null>()
const changeListeners = new Set<() => void>()
/** Writes that landed. The student doc may not have caught up yet. */
const landed = new Set<string>()
/** Attempts that failed or were discarded with nothing usable to show. */
const failedAttempts = new Set<string>()
/** Fuller sets that already got their one retry. */
const coverRetries = new Set<string>()
/**
 * Bumped when the last review unmounts. A job records the session it
 * started in, so a failure after that unmount is not kept.
 */
let reviewSession = 0
const jobSession = new Map<string, number>()
/** The mounted review's moments. A save reuses them so its key matches. */
let reviewMoments: readonly FamilyRepeatMoment[] | null = null
/**
 * Notes a save read back while a review was open. The review's listener can
 * still hold the older text, so the review uses these until it catches up.
 * Otherwise the review would judge the older set and its write would fail.
 */
const savedNotes = new Map<string, FamilyRepeatMoment>()
let savedNotesVersion = 0

const listenerCaughtUp = (
  listened: FamilyRepeatMoment,
  saved: FamilyRepeatMoment
): boolean => {
  if (listened.lastUpdatedAt && saved.lastUpdatedAt) {
    return listened.lastUpdatedAt >= saved.lastUpdatedAt
  }
  return (
    listened.note === saved.note &&
    listened.generatedNote === saved.generatedNote &&
    listened.familyNoteSource === saved.familyNoteSource &&
    listened.category === saved.category &&
    listened.dontShare === saved.dontShare
  )
}

const withSavedNotes = (
  moments: readonly FamilyRepeatMoment[]
): readonly FamilyRepeatMoment[] => {
  if (savedNotes.size === 0) return moments
  let changed = false
  const next = moments.map((moment) => {
    const saved = savedNotes.get(moment.id)
    if (!saved || listenerCaughtUp(moment, saved)) return moment
    changed = true
    return saved
  })
  return changed ? next : moments
}

const pump = () => {
  while (running < MAX_IN_FLIGHT && waiting.length > 0) {
    const start = waiting.shift()
    if (!start) return
    running += 1
    start()
  }
}

const withSlot = <T>(fn: () => Promise<T>): Promise<T> =>
  new Promise((resolve, reject) => {
    waiting.push(() => {
      fn()
        .then(resolve, reject)
        .finally(() => {
          running -= 1
          pump()
        })
    })
    pump()
  })

const cacheKey = (studentId: string, key: string): string =>
  `${studentId}\0${key}`

const notifyChange = () => changeListeners.forEach((listener) => listener())

/** Clears in-memory judge state. Tests use this between cases. */
export const resetFamilyRepeatJudgmentForTests = (): void => {
  running = 0
  waiting.length = 0
  jobs.clear()
  settled.clear()
  changeListeners.clear()
  landed.clear()
  failedAttempts.clear()
  coverRetries.clear()
  reviewSession = 0
  jobSession.clear()
  reviewMoments = null
  savedNotes.clear()
  savedNotesVersion = 0
}

/**
 * One attempt per set key for this page load, plus one retry when the
 * stored key is a smaller moment set this input covers. A stored judgment
 * that already answers every note in this set does not need a judge.
 */
export const familyRepeatShouldJudge = (input: {
  storedKey: string | null
  inputKey: string
  storedIds: readonly string[] | null
  inputIds: readonly string[]
  attempted: boolean
  failed: boolean
  inFlight: boolean
  landed?: boolean
}): boolean => {
  if (input.landed) return false
  if (familyRepeatKeyCovers(input.storedKey, input.inputKey)) return false
  if (input.failed || input.inFlight) return false
  if (
    input.attempted &&
    input.storedKey != null &&
    familyRepeatIdsAreCoveredBy(input.storedIds, input.inputIds)
  ) {
    return true
  }
  if (input.attempted) return false
  return true
}

const markFailed = (key: string) => {
  settled.delete(key)
  landed.delete(key)
  const started = jobSession.get(key)
  // The review that started this job is gone. Recording the failure would
  // make the next mount refuse to judge.
  if (started !== undefined && started !== reviewSession) {
    failedAttempts.delete(key)
    coverRetries.delete(key)
    return
  }
  failedAttempts.add(key)
}

const ensureFamilyRepeatJudgment = (
  studentId: string,
  input: FamilyRepeatInput,
  observedKey: string | null
): Promise<void> => {
  const key = cacheKey(studentId, input.key)
  const existing = jobs.get(key)
  if (existing) return existing
  if (settled.has(key)) return Promise.resolve()
  // Record the attempt before the model call. observedKey stays the key
  // read just now. A later stored key is not copied in for a retry.
  settled.set(key, observedKey)
  jobSession.set(key, reviewSession)
  const job = withSlot(async () => {
    try {
      const judged = await GptHandler.judgeFamilyRepeat({
        messages: input.messages,
      })
      const stored: FamilyRepeatJudgment = {
        key: input.key,
        momentIds: [...input.momentIds],
        judgedAt: new Date().toISOString(),
        source: judged.source,
        messages: judged.messages.map((row) => ({
          momentId: row.id,
          repeats: row.repeats,
          similarMomentIds: row.similarIds,
          reason: row.reason,
        })),
      }
      const result =
        await Firebase.getInstance().writeFamilyRepeatJudgmentIfKeyMatches(
          studentId,
          input.momentIds,
          input.key,
          stored,
          observedKey
        )
      if (result.status === "saved" || result.status === "current") {
        landed.add(key)
        return
      }
      const storedKey = result.storedKey
      // A different key this input does not cover is still a usable judgment.
      if (
        storedKey &&
        !familyRepeatIdsAreCoveredBy(result.storedIds, input.momentIds)
      ) {
        return
      }
      // Nothing stored, or the one retry of a smaller set already ran.
      if (!storedKey || coverRetries.has(key)) markFailed(key)
    } catch (err) {
      markFailed(key)
      throw err
    }
  }).finally(() => {
    jobs.delete(key)
    jobSession.delete(key)
  })
  jobs.set(key, job)
  return job
}

const storedJudgmentKey = (data: unknown): string | null => {
  if (!data || typeof data !== "object") return null
  const field = (data as { familyRepeatJudgment?: { key?: unknown } })
    .familyRepeatJudgment
  return typeof field?.key === "string" && field.key.length > 0
    ? field.key
    : null
}

/**
 * Runs the judge for one student after a note's text was saved. The saved
 * moment is read back, so the set holds the new text. With a review open,
 * the other notes are the review's own and the review takes the saved note
 * before its listener does, so both judge one key and only one model call
 * runs. Without one (Stream), the set
 * is that student's notes from the last 3 months.
 */
export const judgeFamilyRepeatAfterSave = async (
  studentId: string,
  momentId: string
): Promise<void> => {
  const firebase = Firebase.getInstance()
  const saved = await firebase.getDocumentById<FamilyRepeatMoment>(
    DOC_TYPES.STANDOUT_MOMENTS,
    momentId
  )
  if (!saved || saved.studentId !== studentId) return
  const fromReview = reviewMoments
  let moments: FamilyRepeatMoment[]
  if (fromReview) {
    savedNotes.set(momentId, saved)
    savedNotesVersion += 1
    notifyChange()
    moments = withSavedNotes(fromReview).filter(
      (moment) => moment.studentId === studentId && moment.id !== momentId
    )
  } else {
    const since = historyWindowStart(Date.now())
    const all = await firebase.variableQuery<FamilyRepeatMoment>(
      DOC_TYPES.STANDOUT_MOMENTS,
      { queries: { studentId: { value: studentId } } }
    )
    moments = all.filter((moment) => {
      if (moment.id === momentId) return false
      const at = Date.parse(moment.createdAt ?? "")
      return !Number.isNaN(at) && at >= since
    })
  }
  const input = familyRepeatInputFor([...moments, saved])
  if (!input) return
  const student = await firebase.getDocumentById<Record<string, unknown>>(
    DOC_TYPES.STUDENTS,
    studentId
  )
  const storedKey = storedJudgmentKey(student)
  if (familyRepeatKeyCovers(storedKey, input.key)) return
  try {
    await ensureFamilyRepeatJudgment(studentId, input, storedKey)
  } finally {
    notifyChange()
  }
}

export type FamilyRepeatPhase = "checking" | "ready" | "failed"

/**
 * One student's judgment for the review. Stable for other controls:
 * `similarByMomentId` is the source of truth for "is this note similar".
 */
export type FamilyRepeatStudentView = {
  phase: FamilyRepeatPhase
  /** Stored rows for notes in this set. Similar ids are one-way here. */
  verdicts: readonly FamilyRepeatVerdict[]
  /** This student's family-facing notes that were compared. */
  momentIds: readonly string[]
  /** Both-ways similar ids per moment. A note with none is absent. */
  similarByMomentId: ReadonlyMap<string, readonly string[]>
}

const EMPTY_SIMILAR: ReadonlyMap<string, readonly string[]> = new Map()

const readyView = (
  messages: readonly FamilyRepeatVerdict[],
  momentIds: readonly string[]
): FamilyRepeatStudentView => {
  const inSet = new Set(momentIds)
  return {
    phase: "ready",
    verdicts: messages
      .filter((row) => inSet.has(row.momentId))
      .map((row) => ({
        ...row,
        similarMomentIds: (row.similarMomentIds ?? []).filter((id) =>
          inSet.has(id)
        ),
      })),
    momentIds,
    similarByMomentId: familyRepeatSimilarIds(messages, momentIds),
  }
}

export const useFamilyRepeatJudgments = (
  moments: readonly FamilyRepeatMoment[],
  studentsById: Record<
    string,
    { familyRepeatJudgment?: FamilyRepeatJudgment | null } | undefined
  >
): ReadonlyMap<string, FamilyRepeatStudentView> => {
  const savedVersion = savedNotesVersion
  const inputs = useMemo(() => {
    void savedVersion
    return familyRepeatInputsByStudent(withSavedNotes(moments))
  }, [moments, savedVersion])
  const [failedKeys, setFailedKeys] = useState<ReadonlySet<string>>(
    () => new Set()
  )
  const [tick, setTick] = useState(0)

  useEffect(() => {
    reviewMoments = moments
    for (const moment of moments) {
      const saved = savedNotes.get(moment.id)
      if (saved && listenerCaughtUp(moment, saved)) savedNotes.delete(moment.id)
    }
    return () => {
      if (reviewMoments === moments) reviewMoments = null
    }
  }, [moments])

  useEffect(() => {
    const onChange = () => setTick((current) => current + 1)
    changeListeners.add(onChange)
    return () => {
      changeListeners.delete(onChange)
      if (changeListeners.size > 0) return
      // The review is gone. Failures already recorded are cleared, and so
      // is the settled flag of anything still in flight. A job that fails
      // after this sees a new session and does not record another failure.
      reviewSession += 1
      savedNotes.clear()
      for (const key of [...failedAttempts]) {
        failedAttempts.delete(key)
        settled.delete(key)
        landed.delete(key)
        coverRetries.delete(key)
      }
      for (const key of jobs.keys()) {
        if (jobSession.get(key) === reviewSession) continue
        settled.delete(key)
        coverRetries.delete(key)
      }
    }
  }, [])

  useEffect(() => {
    let active = true
    for (const [studentId, input] of inputs) {
      const stored = studentsById[studentId]?.familyRepeatJudgment
      const storedKey = stored?.key ?? null
      const storedIds = stored?.momentIds ?? null
      const key = cacheKey(studentId, input.key)
      if (
        !familyRepeatShouldJudge({
          storedKey,
          inputKey: input.key,
          storedIds,
          inputIds: input.momentIds,
          attempted: settled.has(key),
          failed: failedKeys.has(key) || failedAttempts.has(key),
          inFlight: jobs.has(key) && jobSession.get(key) === reviewSession,
          landed: landed.has(key),
        })
      ) {
        continue
      }
      const coverRetry =
        settled.has(key) &&
        storedKey != null &&
        familyRepeatIdsAreCoveredBy(storedIds, input.momentIds)
      if (coverRetry && coverRetries.has(key)) {
        markFailed(key)
        setFailedKeys((current) => {
          if (current.has(key)) return current
          const next = new Set(current)
          next.add(key)
          return next
        })
        continue
      }
      if (coverRetry) {
        coverRetries.add(key)
        settled.delete(key)
      }
      void ensureFamilyRepeatJudgment(studentId, input, storedKey).then(
        () => {
          if (active) setTick((current) => current + 1)
        },
        (err: unknown) => {
          const detail = err instanceof Error ? err.message : String(err)
          console.error("[standout] family repeat judgment failed", detail)
          if (!active) return
          // A job from the previous mount does not fail this one. Tick so
          // this mount can judge again.
          if (!failedAttempts.has(key)) {
            setTick((current) => current + 1)
            return
          }
          setFailedKeys((current) => {
            const next = new Set(current)
            next.add(key)
            return next
          })
        }
      )
    }
    return () => {
      active = false
    }
  }, [failedKeys, inputs, studentsById, tick])

  return useMemo(() => {
    void tick
    const views = new Map<string, FamilyRepeatStudentView>()
    for (const [studentId, input] of inputs) {
      const stored = studentsById[studentId]?.familyRepeatJudgment
      const key = cacheKey(studentId, input.key)
      const covered = familyRepeatIdsAreCoveredBy(
        stored?.momentIds,
        input.momentIds
      )
      if (stored && familyRepeatKeyCovers(stored.key, input.key)) {
        views.set(studentId, readyView(stored.messages, input.momentIds))
        continue
      }
      const pending = {
        verdicts: [],
        momentIds: input.momentIds,
        similarByMomentId: EMPTY_SIMILAR,
      }
      // `settled` is set when a judge starts, so a running or just-written
      // judge for this set wins over an older stored judgment.
      if (jobs.has(key) || landed.has(key)) {
        views.set(studentId, { phase: "checking", ...pending })
        continue
      }
      if (settled.has(key) && stored?.key && !covered) {
        views.set(studentId, readyView(stored.messages, input.momentIds))
        continue
      }
      const retryOpen =
        settled.has(key) && !!stored?.key && covered && !coverRetries.has(key)
      const failed =
        failedKeys.has(key) ||
        failedAttempts.has(key) ||
        (settled.has(key) && !retryOpen)
      views.set(studentId, {
        phase: failed ? "failed" : "checking",
        ...pending,
      })
    }
    return views
  }, [failedKeys, inputs, studentsById, tick])
}

/** One note's answer, for the spine label and the share confirm. */
export type FamilyRepeatMomentView = {
  /** "none" when this note is not in a judged set (no view, or alone). */
  phase: FamilyRepeatPhase | "none"
  similar: boolean
  /** Other notes for the same student that are too close. */
  similarMomentIds: readonly string[]
  /** The model's sentence for this pair, when there is one. */
  reason: string | null
}

const NO_REPEAT: FamilyRepeatMomentView = {
  phase: "none",
  similar: false,
  similarMomentIds: [],
  reason: null,
}

export const familyRepeatMomentView = (
  view: FamilyRepeatStudentView | undefined,
  momentId: string
): FamilyRepeatMomentView => {
  if (!view || !view.momentIds.includes(momentId)) return NO_REPEAT
  const similarMomentIds = view.similarByMomentId.get(momentId) ?? []
  const own = view.verdicts.find((row) => row.momentId === momentId)
  const partner = view.verdicts.find(
    (row) => row.repeats && (row.similarMomentIds ?? []).includes(momentId)
  )
  const reason =
    own && (own.repeats || similarMomentIds.length === 0)
      ? own.reason
      : (partner?.reason ?? own?.reason ?? null)
  return {
    phase: view.phase,
    similar: similarMomentIds.length > 0,
    similarMomentIds,
    reason,
  }
}
